import { EmblaCarouselType } from "embla-carousel";
import { AutoplayType } from 'embla-carousel-autoplay';

/**
 * Adds click handlers to previous and next buttons for an Embla Carousel.
 * Handles scrolling, autoplay reset, and button disabled state.
 *
 * @param emblaApi - The Embla Carousel API instance.
 * @param prevBtn - The HTML element for the previous button.
 * @param nextBtn - The HTML element for the next button.
 * @returns A cleanup function to remove event listeners.
 */
const addPrevNextBtnsClickHandlers = (emblaApi: EmblaCarouselType, prevBtn: HTMLElement, nextBtn: HTMLElement): () => void => {

    if (!prevBtn && !nextBtn) {
        return () => { };
    }

    const autoplay = emblaApi.plugins().autoplay as AutoplayType | undefined;

    const scrollPrev = () => {
        emblaApi.scrollPrev();
        autoplay?.reset(); // Reset autoplay timer on user interaction
    }

    const scrollNext = () => {
        emblaApi.scrollNext();
        autoplay?.reset(); // Reset autoplay timer on user interaction
    }

    prevBtn?.addEventListener('click', scrollPrev);
    nextBtn?.addEventListener('click', scrollNext);

    const togglePrevNextBtnsState = (): void => {
        // Disable prev button if cannot scroll prev
        if (emblaApi.canScrollPrev()) {
            prevBtn?.removeAttribute('disabled');
        } else {
            prevBtn?.setAttribute('disabled', 'disabled');
        }

        // Disable next button if cannot scroll next
        if (emblaApi.canScrollNext()) {
            nextBtn?.removeAttribute('disabled');
        } else {
            nextBtn?.setAttribute('disabled', 'disabled');
        }
    }

    emblaApi.on('select', togglePrevNextBtnsState).on('init', togglePrevNextBtnsState).on('reInit', togglePrevNextBtnsState);

    return () => {
        prevBtn?.removeEventListener('click', scrollPrev);
        nextBtn?.removeEventListener('click', scrollNext);
        emblaApi.off('select', togglePrevNextBtnsState).off('init', togglePrevNextBtnsState).off('reInit', togglePrevNextBtnsState);
    }
}


/**
 * Adds click handlers to dot navigation for an Embla Carousel.
 * Handles scrolling to specific slides and updating the active dot class.
 *
 * @param emblaApi - The Embla Carousel API instance.
 * @param dotsNode - An array of HTML elements representing the dots.
 * @returns A cleanup function to remove event listeners.
 */
const addEmblaDotClickHandlers = (emblaApi: EmblaCarouselType, dotsNode: HTMLElement[] | undefined): () => void => {

    if (!dotsNode || dotsNode.length === 0) {
        return () => { };
    }

    const autoplay = emblaApi.plugins().autoplay as AutoplayType | undefined;
    const dotCleanups: (() => void)[] = [];

    dotsNode?.forEach((dot, index) => {
        const handler = () => {
            emblaApi.scrollTo(index);
            autoplay?.reset(); // Reset autoplay timer on user interaction
        }
        dot.addEventListener('click', handler);
        dotCleanups.push(() => dot.removeEventListener('click', handler));
    });

    const toggleDotsCurrent = () => {

        const selected = emblaApi.selectedScrollSnap();

        // Remove 'current' class from all dots
        dotsNode?.forEach(dot => dot.classList.remove('current'));

        // Add 'current' class to the selected dot
        if (dotsNode?.[selected]) {
            dotsNode[selected].classList.add('current');
        }

    };

    emblaApi.on('init', toggleDotsCurrent)
        .on('reInit', toggleDotsCurrent)
        .on('select', toggleDotsCurrent);

    return () => {
        dotCleanups.forEach(cleanup => cleanup());
        emblaApi.off('init', toggleDotsCurrent)
            .off('reInit', toggleDotsCurrent)
            .off('select', toggleDotsCurrent);
    }
}

export { addPrevNextBtnsClickHandlers, addEmblaDotClickHandlers }

